import { useEffect, useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/cn";

type MobileSheetProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** full = nearly full viewport; bottom = bottom sheet (~90vh) */
  variant?: "full" | "bottom";
  className?: string;
  /** Extra actions in the sheet header (right of title, left of close) */
  headerActions?: ReactNode;
  /** Reserve space for the mobile bottom tab bar (default true). */
  reserveBottomNav?: boolean;
};

/**
 * Mobile overlay sheet: scrim, Escape, body scroll-lock, safe areas.
 * Intended for < md filter panels, thread lists, etc.
 */
export function MobileSheet({
  open,
  onClose,
  title,
  children,
  variant = "full",
  className,
  headerActions,
  reserveBottomNav = true,
}: MobileSheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => {
      panelRef.current
        ?.querySelector<HTMLElement>("button, [href], input, select, textarea")
        ?.focus();
    }, 60);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] md:hidden" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "absolute inset-x-0 flex flex-col border-border bg-surface shadow-xl",
          variant === "full"
            ? "inset-y-0 left-0 right-0"
            : "bottom-0 max-h-[90vh] rounded-t-2xl border-t",
          className,
        )}
        style={
          variant === "full"
            ? {
                paddingTop: "env(safe-area-inset-top)",
                paddingBottom: reserveBottomNav
                  ? "calc(3.5rem + env(safe-area-inset-bottom))"
                  : "env(safe-area-inset-bottom)",
              }
            : {
                paddingBottom: reserveBottomNav
                  ? "calc(3.5rem + env(safe-area-inset-bottom))"
                  : "env(safe-area-inset-bottom)",
              }
        }
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
          <h2
            id={titleId}
            className="min-w-0 flex-1 truncate text-base font-semibold text-foreground"
          >
            {title}
          </h2>
          {headerActions}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md p-1.5 text-muted transition-colors hover:bg-surface-hover hover:text-foreground"
          >
            <X className="size-5" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </div>
  );
}
